import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import {
  getAvitoStatus,
  saveAvitoCredentials,
  deleteAvitoCredentials,
  type AvitoStatus,
} from '../../services/avitoService';

const AVITO_REGISTRATION_URL = 'https://www.avito.ru/professionals/api';

const AvitoAuthContent: React.FC = () => {
  const colors = useBrandingColors();
  const { isOwner, isAdmin, isSuperAdmin } = useRoleAccess();

  const [status, setStatus] = useState<AvitoStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // canManage с бэка авторитетный; локальная роль нужна как fallback
  // при ошибке статуса. Серверная проверка всё равно остаётся главной
  // (не показывать форму тем, кому всё равно нельзя).
  const localCanManage = Boolean(isOwner || isAdmin || isSuperAdmin);
  const canManage = status?.canManage ?? localCanManage;

  useEffect(() => {
    void fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const data = await getAvitoStatus();
      setStatus(data);
    } catch (error) {
      console.error('Failed to fetch Avito status', error);
      setStatus({
        connected: false,
        tokenExpiresAt: null,
        clientIdMasked: null,
        canManage: localCanManage,
      });
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSave = async () => {
    const trimmedId = clientId.trim();
    const trimmedSecret = clientSecret.trim();
    if (!trimmedId || !trimmedSecret) {
      toast.error('Введите client_id и client_secret');
      return;
    }

    setSaving(true);
    try {
      await saveAvitoCredentials(trimmedId, trimmedSecret);
      toast.success('Авито подключено');
      setClientId('');
      setClientSecret('');
      await fetchStatus();
    } catch (error: any) {
      const code = error?.response?.data?.code;
      const message = error?.response?.data?.message;
      if (code === 'invalid_credentials') {
        toast.error(message || 'Авито отверг переданные client_id или client_secret');
      } else if (code === 'avito_unavailable' || code === 'avito_malformed_response') {
        toast.error(message || 'Сервис авторизации Авито временно недоступен');
      } else {
        toast.error('Не удалось сохранить настройки Авито');
      }
      console.error('Failed to save Avito credentials', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Отключить интеграцию с Авито? Сохранённые ключи и токен будут удалены.')) {
      return;
    }
    setDisconnecting(true);
    try {
      await deleteAvitoCredentials();
      toast.success('Авито отключено');
      await fetchStatus();
    } catch (error) {
      console.error('Failed to disconnect Avito', error);
      toast.error('Не удалось отключить интеграцию');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loadingStatus) {
    return (
      <div className="text-center py-8">
        <div
          className="inline-block animate-spin rounded-full h-10 w-10 border-b-2"
          style={{ borderBottomColor: colors.primary }}
        />
        <p className="mt-2 text-gray-600">Проверка статуса подключения...</p>
      </div>
    );
  }

  const connected = status?.connected ?? false;

  return (
    <div className="space-y-6">
      {/* Карточка статуса — видна всем сотрудникам компании */}
      <div
        className={`flex items-center gap-3 p-4 rounded-xl border ${
          connected
            ? 'bg-green-50 text-green-800 border-green-200'
            : 'bg-yellow-50 text-yellow-800 border-yellow-200'
        }`}
      >
        {connected ? (
          <CheckCircleIcon className="h-6 w-6 flex-shrink-0" />
        ) : (
          <XCircleIcon className="h-6 w-6 flex-shrink-0" />
        )}
        <div className="flex-1">
          <p className="font-medium">
            {connected ? 'Авито подключено' : 'Авито не подключено'}
          </p>
          {connected && status?.clientIdMasked && (
            <p className="text-sm mt-0.5 opacity-80">
              client_id: {status.clientIdMasked}
            </p>
          )}
        </div>
        {connected && canManage && (
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {disconnecting ? 'Отключение...' : 'Отключить'}
          </button>
        )}
      </div>

      {!canManage && !connected && (
        <div className="rounded-xl p-4 border border-gray-200 bg-gray-50 text-sm text-gray-700">
          Подключение Авито доступно только владельцу компании,
          администратору или супер-администратору. Обратитесь к ним, чтобы
          настроить интеграцию.
        </div>
      )}

      {canManage && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-md font-semibold text-gray-900 mb-1">
            {connected ? 'Обновить ключи приложения' : 'Подключить Авито'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Зарегистрируйте приложение в{' '}
            <a
              href={AVITO_REGISTRATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: colors.primary }}
            >
              кабинете Авито
            </a>{' '}
            и скопируйте сюда client_id и client_secret. Сохранённые ключи мы
            проверяем сразу: если Авито их не примет, ничего не запишется.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder={connected ? 'Введите новый client_id' : 'client_id из кабинета Авито'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
                autoComplete="off"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Secret
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={connected ? 'Введите новый client_secret' : 'client_secret из кабинета Авито'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 font-mono"
                style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
                autoComplete="off"
              />
              {connected && (
                <p className="mt-1 text-xs text-gray-500">
                  Текущий секрет хранится на сервере и не отображается. Чтобы
                  оставить его без изменений — закройте страницу.
                </p>
              )}
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !clientId.trim() || !clientSecret.trim()}
              className="px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => !saving && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
              onMouseLeave={(e) => !saving && (e.currentTarget.style.backgroundColor = colors.primary)}
            >
              {saving ? 'Проверка и сохранение...' : connected ? 'Обновить' : 'Подключить'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvitoAuthContent;
